/**
 * Which preservation journey the post-cycle Home speaks about.
 *
 * Extracted from `PlanHomeScreen` so the rule is testable: that module is a
 * screen and cannot be imported by a browserless test, which is why nothing
 * caught the bug this file exists to prevent.
 *
 * The bug: an embryo-freezing cycle's Home greeted the patient with "9 ovocytes
 * sont stockés". A preservation cycle is closed by `useCompleteCycle` with a
 * deliberately null outcome, so it has no `outcomeId`, so
 * `shouldUseCycleEndingForHomeExperience` returns false and the ended cycle
 * stops feeding `context.treatment.cycleType`. With no cycle underway either,
 * the type read as "unknown" and the variant fell through to the patient's
 * stated *goal* — egg freezing for her — while the vault directly below
 * correctly listed embryos. The page contradicted itself.
 */

import type {
  CycleType,
  TreatmentGoal,
} from "@src/packages/experience/experience.types";

export type PostCycleStorageVariant =
  | "egg-freezing-postcycle"
  | "embryo-freezing-postcycle"
  | "sperm-freezing-postcycle";

export type PostCycleStorageInput = {
  /**
   * The type of the closed cycle this Home is about, when one is on record.
   * A raw string: `HomeCycleEnding.endedCycleType` is typed `string | null` by
   * its own contract, and narrowing it here beats adding a third private copy
   * of `asCycleType`.
   */
  endedCycleType?: string | null;
  /**
   * The type the experience reports. Usually about an active cycle — so
   * "unknown" here — but it also carries the `dev_cycleType` override and the
   * canonical projection, which is why an explicit value outranks the ended
   * cycle rather than the other way round.
   */
  cycleType?: string | null;
  /** The patient's stated goal. Evidence of last resort. */
  treatmentGoal?: string | null;
};

/**
 * The two vocabularies this rule reads, tied to their source unions by
 * `satisfies` so a rename upstream fails the build here instead of silently
 * matching nothing and resolving every patient to egg freezing.
 *
 * The goal map carries both spellings: the vocabulary has `-freezing` and
 * `-preserve` for the same three journeys, and the code this replaced tested
 * only the first — so an `embryo-preserve` patient was shown the egg page.
 */
const VARIANT_BY_CYCLE_TYPE = {
  egg_freezing: "egg-freezing-postcycle",
  embryo_freezing: "embryo-freezing-postcycle",
  sperm_freezing: "sperm-freezing-postcycle",
} as const satisfies Partial<Record<CycleType, PostCycleStorageVariant>>;

const VARIANT_BY_GOAL = {
  "egg-freezing": "egg-freezing-postcycle",
  "egg-preserve": "egg-freezing-postcycle",
  "embryo-freezing": "embryo-freezing-postcycle",
  "embryo-preserve": "embryo-freezing-postcycle",
  "sperm-freezing": "sperm-freezing-postcycle",
  "sperm-preserve": "sperm-freezing-postcycle",
} as const satisfies Partial<Record<TreatmentGoal, PostCycleStorageVariant>>;

/** A cast-free lookup: the entries widen to the value union, which is the type wanted. */
function lookup(
  table: Record<string, PostCycleStorageVariant>,
  value: string | null | undefined,
): PostCycleStorageVariant | null {
  if (!value) return null;
  for (const [key, variant] of Object.entries(table)) {
    if (key === value) return variant;
  }
  return null;
}

/**
 * The journey a preservation post-cycle surface should speak about, or null when
 * nothing on record names one.
 *
 * Order of evidence: an explicitly named cycle type, then the closed cycle's own
 * type, then the goal. The goal comes last because it is an intention, not a
 * fact, and reading it first is what mislabelled the page — in both directions,
 * since the old ordering tested the goal before the type.
 */
export function resolvePreservationPostCycleVariant({
  endedCycleType,
  cycleType,
  treatmentGoal,
}: PostCycleStorageInput): PostCycleStorageVariant | null {
  const named = cycleType && cycleType !== "unknown" ? cycleType : null;
  return (
    lookup(VARIANT_BY_CYCLE_TYPE, named) ??
    lookup(VARIANT_BY_CYCLE_TYPE, endedCycleType) ??
    lookup(VARIANT_BY_GOAL, treatmentGoal)
  );
}

/**
 * The same rule for the `postcycle_storage` scenario, which is a preservation
 * storage state by definition and therefore always owes the patient a page.
 */
export function resolvePostCycleStorageVariant(
  input: PostCycleStorageInput,
): PostCycleStorageVariant {
  return resolvePreservationPostCycleVariant(input) ?? "egg-freezing-postcycle";
}
